// src/components/Admin/TaskManager/ScheduledTasksTab.tsx

import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useScheduledTasks } from '../../../hooks/useScheduledTasks';
import TaskDialog from './TaskDialog';
import { ScheduledTask, TASK_STATUS_OPTIONS } from '../../../types/taskManager';
import { format } from 'date-fns';

const ScheduledTasksTab: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ScheduledTask | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTask, setMenuTask] = useState<ScheduledTask | null>(null);

  const {
    tasks,
    loading,
    refetch,
    createTask,
    updateTask,
    deleteTask,
    runNow,
    enable,
    disable,
    pause,
    resume,
    isCreating,
    isUpdating,
    isRunning,
  } = useScheduledTasks({ status: statusFilter || undefined });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: ScheduledTask) => {
    setAnchorEl(event.currentTarget);
    setMenuTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTask(null);
  };

  const handleEdit = (task: ScheduledTask) => {
    setSelectedTask(task);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleAdd = () => {
    setSelectedTask(null);
    setDialogOpen(true);
  };

  const handleSave = async (data: Partial<ScheduledTask>) => {
    try {
      if (selectedTask) {
        await updateTask({ ...data, id: selectedTask.id });
      } else {
        await createTask(data);
      }
      setDialogOpen(false);
      setSelectedTask(null);
    } catch (error: any) {
      alert(`Error: ${error.message || 'Failed to save task'}`);
    }
  };

  const handleDelete = async (task: ScheduledTask) => {
    if (window.confirm(`Delete task "${task.name}"?\n\nThis cannot be undone.`)) {
      try {
        await deleteTask(task.id);
        handleMenuClose();
      } catch (error: any) {
        alert(`Error: ${error.message || 'Failed to delete task'}`);
      }
    }
  };

  const handleRunNow = async (task: ScheduledTask) => {
    if (window.confirm(`Run "${task.name}" immediately?\n\nThis will create a new job and submit it to the queue.`)) {
      try {
        await runNow(task.id);
        handleMenuClose();
      } catch (error: any) {
        alert(`Error: ${error.message || 'Failed to run task'}`);
      }
    }
  };

  const handleToggleEnabled = async (task: ScheduledTask) => {
    try {
      if (task.enabled) {
        await disable(task.id);
      } else {
        await enable(task.id);
      }
      handleMenuClose();
    } catch (error: any) {
      alert(`Error: ${error.message || 'Failed to toggle enabled state'}`);
    }
  };

  const handleTogglePause = async (task: ScheduledTask) => {
    try {
      if (task.status === 'paused') {
        await resume(task.id);
      } else {
        await pause(task.id);
      }
      handleMenuClose();
    } catch (error: any) {
      alert(`Error: ${error.message || 'Failed to toggle pause state'}`);
    }
  };

  const handleDuplicate = (task: ScheduledTask) => {
    const duplicated = {
      ...task,
      name: `${task.name} (Copy)`,
      enabled: false,
      status: 'paused' as any,
    };
    delete (duplicated as any).id;
    delete (duplicated as any).created_at;
    delete (duplicated as any).updated_at;
    delete (duplicated as any).last_run;
    delete (duplicated as any).next_run;
    delete (duplicated as any).run_count;
    delete (duplicated as any).failure_count;

    setSelectedTask(duplicated as ScheduledTask);
    setDialogOpen(true);
    handleMenuClose();
  };

  const getStatusColor = (status: string) => {
    const option = TASK_STATUS_OPTIONS.find(opt => opt.value === status);
    return option?.color || '#9e9e9e';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const isOverdue = (task: ScheduledTask) => {
    if (!task.next_run || !task.enabled || task.status !== 'active') return false;
    return new Date(task.next_run) < new Date();
  };

  return (
    <Box>
      {/* Actions Bar */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="large"
        >
          Create Task
        </Button>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Filter:
          </Typography>
          <Button
            size="small"
            onClick={() => setStatusFilter('')}
            variant={statusFilter === '' ? 'contained' : 'outlined'}
          >
            All
          </Button>
          {TASK_STATUS_OPTIONS.map((opt) => (
            <Button
              key={opt.value}
              size="small"
              onClick={() => setStatusFilter(opt.value)}
              variant={statusFilter === opt.value ? 'contained' : 'outlined'}
            >
              {opt.label}
            </Button>
          ))}

          <IconButton onClick={() => refetch()} size="small" sx={{ ml: 1 }}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Tasks Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : tasks.length === 0 ? (
        <Alert severity="info">
          No scheduled tasks found. Click &quot;Create Task&quot; to add one.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Job Type</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell>Last Run</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Runs</TableCell>
                <TableCell align="center">Failures</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((task) => (
                <TableRow
                  key={task.id}
                  sx={{
                    bgcolor: isOverdue(task) ? 'rgba(255, 152, 0, 0.1)' : 'inherit'
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {task.name}
                    </Typography>
                    {task.description && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {task.description}
                      </Typography>
                    )}
                    {task.tags && task.tags.length > 0 && (
                      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {task.tags.map(tag => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                        ))}
                      </Box>
                    )}
                  </TableCell>

                  <TableCell>
                    <Chip label={task.job_type} size="small" variant="outlined" />
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" display="block">
                      {task.interval_type === 'cron'
                        ? `Cron: ${task.cron_expression}`
                        : `${task.interval_type} (×${task.interval_value})`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.timezone_name}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" display="block">
                      {formatDate(task.next_run)}
                    </Typography>
                    {isOverdue(task) && (
                      <Chip label="Overdue" size="small" color="warning" sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }} />
                    )}
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(task.last_run)}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={task.status}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(task.status),
                          color: 'white',
                          fontSize: '0.7rem',
                          minWidth: 70,
                        }}
                      />
                      {!task.enabled && (
                        <Chip
                          label="Disabled"
                          size="small"
                          color="error"
                          sx={{ fontSize: '0.65rem', height: 18 }}
                        />
                      )}
                    </Box>
                  </TableCell>

                  <TableCell align="center">
                    <Typography variant="body2">
                      {task.run_count}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label={task.failure_count}
                      size="small"
                      color={task.failure_count > 0 ? 'error' : 'default'}
                      sx={{ minWidth: 40 }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Run Now">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleRunNow(task)}
                            color="primary"
                            disabled={isRunning || !task.enabled}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(task)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, task)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {menuTask && (
          <>
            <MenuItem onClick={() => menuTask && handleRunNow(menuTask)} disabled={!menuTask?.enabled}>
              <ListItemIcon><PlayArrowIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Run Now</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => menuTask && handleTogglePause(menuTask)}>
              <ListItemIcon>
                {menuTask?.status === 'paused' ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{menuTask?.status === 'paused' ? 'Resume' : 'Pause'}</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => menuTask && handleToggleEnabled(menuTask)}>
              <ListItemIcon>
                {menuTask?.enabled ? <StopIcon fontSize="small" /> : <PowerSettingsNewIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>{menuTask?.enabled ? 'Disable' : 'Enable'}</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => menuTask && handleDuplicate(menuTask)}>
              <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => menuTask && handleEdit(menuTask)}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>

            <MenuItem onClick={() => menuTask && handleDelete(menuTask)} sx={{ color: 'error.main' }}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create/Edit Dialog */}
      <TaskDialog
        open={dialogOpen}
        task={selectedTask}
        onClose={() => {
          setDialogOpen(false);
          setSelectedTask(null);
        }}
        onSave={handleSave}
        isSaving={isCreating || isUpdating}
      />
    </Box>
  );
};

export default ScheduledTasksTab;